import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'

import { BUILDING_TOOLTIP_ID, DIRECTIONS, KEY_CODES } from 'constants'

import { CLAN_LANDING } from 'urls'

import { canManageBuilding } from 'helpers/accounts'

import { BACK_TO_CLAN_INFO } from './constants'

import { Back } from 'react-wot-bootstrap'

import BuildingTooltip from 'components/BuildingTooltip/BuildingTooltip.jsx'
import CommandCenterTooltip from 'components/CommandCenterTooltip/CommandCenterTooltip.jsx'
import Gamefield from 'components/Gamefield/Gamefield.jsx'
import StartingPoint from 'components/StartingPoint/StartingPoint.jsx'
import LoadingScreen from 'components/LoadingScreen/LoadingScreen.jsx'

import ClanPanel from 'containers/ClanPanel/ClanPanel.jsx'
import MigrationScreen from 'containers/MigrationScreen/MigrationScreen.jsx'

import { ClanInfoPanel, CommandCenter, Direction, RatingLabel, SlotTooltip, Toolbar } from './components'
import { constructionAnimated } from 'state/actions/building_construction'
import { playBuildingClickSound, playExitStrongholdSound, playHoverSound } from 'state/actions/sounds'
import { openLeaderboard } from 'state/actions/leaderboard'
import { requestStronghold } from 'state/actions/stronghold'
import { showMigrationScreen } from 'state/actions/migration_screen'
import { requestClientCounters } from 'state/actions/client_counters'

import styles from './Stronghold.scss'
import { requestShopStatus } from '../../state/actions/shop'

export class Stronghold extends React.Component {
    static propTypes = {
        // state props
        level: PropTypes.number.isRequired,
        showLoading: PropTypes.bool.isRequired,
        slots: PropTypes.arrayOf(PropTypes.object).isRequired,
        clanId: PropTypes.number.isRequired,
        isOwn: PropTypes.bool.isRequired,
        isShopStateLoaded: PropTypes.bool.isRequired,
        clanInfo: PropTypes.object,
        clanStats: PropTypes.object,
        popoverOpened: PropTypes.bool.isRequired,
        userFlags: PropTypes.object.isRequired,
        clientCounters: PropTypes.object.isRequired,
        elo_leaderboards: PropTypes.array,
        hasPermission: PropTypes.bool.isRequired,
        isMigrationScreenVisible: PropTypes.bool.isRequired,

        // ownProps
        ignoreEscape: PropTypes.bool,

        // redux bound actions
        pushState: PropTypes.func.isRequired,
        constructionAnimated: PropTypes.func.isRequired,
        playBuildingClickSound: PropTypes.func.isRequired,
        playHoverSound: PropTypes.func.isRequired,
        openLeaderboard: PropTypes.func.isRequired,
        openClanLanding: PropTypes.func.isRequired,
        onComponentDidMount: PropTypes.func.isRequired,
        playExitStrongholdSound: PropTypes.func.isRequired,
        showMigrationScreen: PropTypes.func.isRequired,
    }

    componentDidMount() {
        this.props.onComponentDidMount(this.props)
        window.addEventListener('keydown', this.onKeyDown)
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (
            this.props.isOwn &&
            !prevProps.clientCounters.isLoaded &&
            this.props.clientCounters.items.clanNews > 0
        ) {
            this.props.showMigrationScreen()
        }
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.onKeyDown)
    }

    onKeyDown = (e) => {
        if (e.keyCode !== KEY_CODES.ESCAPE) return

        const ignoreEscape = (
            this.props.children ||
            this.props.popoverOpened ||
            this.props.isMigrationScreenVisible ||
            this.props.ignoreEscape
        )

        if (ignoreEscape) return

        this.handleOpenclanlanding()
    }

    handleOpenclanlanding = () => {
        this.props.openClanLanding()
        this.props.playExitStrongholdSound()
    }

    getRatingLabels = () => {
        if (!this.props.elo_leaderboards || !this.props.isOwn) return null

        let labels = this.props.elo_leaderboards.map(leaderboard => (
            <RatingLabel
                {...leaderboard}
                key={leaderboard.vehicle_level}
                onClick={() => this.props.openLeaderboard(leaderboard.vehicle_level)}
            />
        ))

        return (
            <div className={styles.ratings}>
                {labels}
            </div>
        )
    }

    getTooltips = () => {
        const tooltips = this.props.slots.map(slot => (
            slot.building ? (
                <BuildingTooltip
                    key={`${BUILDING_TOOLTIP_ID}_${slot.building.type}`}
                    id={`${BUILDING_TOOLTIP_ID}_${slot.building.type}`}
                    building={slot.building}
                    bounds={{ top: 33 }}
                />
            ) : (
                <SlotTooltip
                    key={`slot_tooltip_${slot.id}`}
                    slotId={slot.id}
                    showConstructMessage={this.props.isOwn}
                    hasPermission={this.props.hasPermission}
                />
            )
        ))

        tooltips.push(
            <CommandCenterTooltip
                key='command_center_tooltip'
                level={this.props.level}
            />
        )

        return tooltips
    }

    getDirections = () => {
        let emptySlotClickHandler = null
        let buildingClickHandler = null

        if (this.props.isOwn) {
            emptySlotClickHandler = slotId => {
                this.props.playBuildingClickSound()
                this.props.pushState(`stronghold/build/${slotId}`)
            }

            buildingClickHandler = buildingId => {
                this.props.playBuildingClickSound()
                this.props.pushState(`stronghold/building/${buildingId}`)
            }
        }

        return DIRECTIONS.map(directionName => {
            const directionSlots = this.props.slots
                .filter(slot => (slot.direction === directionName))
                .sort((firstSlot, secondSlot) => (firstSlot.position > secondSlot.position))

            return (
                <Direction
                    key={directionName}
                    level={this.props.level}
                    name={directionName}
                    slots={directionSlots}
                    emptySlotClickHandler={emptySlotClickHandler}
                    buildingClickHandler={buildingClickHandler}
                    emptySlotHoverHandler={this.props.isOwn ? this.props.playHoverSound : null}
                    buildingHoverHandler={this.props.playHoverSound}
                    buildingConstructionAnimated={this.props.constructionAnimated}
                    hasPermission={this.props.hasPermission}
                    isOwn={this.props.isOwn}
                />
            )
        })
    }

    render() {
        if (this.props.showLoading) {
            if (!this.props.isOwn) return <LoadingScreen type='gamefield' />
            return (
                <div className={styles.base}>
                    {this.props.isOwn ?
                        <ClanPanel /> :
                        <ClanInfoPanel
                            stats={this.props.clanStats}
                            clan={this.props.clanInfo}
                            elo_leaderboards={this.props.elo_leaderboards}
                        />
                    }
                    <Gamefield>
                        <LoadingScreen type='gamefield' />
                    </Gamefield>
                </div>
            )
        }

        if (this.props.children) {
            return (
                <div className={styles.base}>
                    {this.props.isOwn && <ClanPanel />}
                    {this.props.children}
                </div>
            )
        }

        let commandCenterClickHandler = null

        if (this.props.isOwn) {
            commandCenterClickHandler = () => {
                this.props.playBuildingClickSound()
                this.props.pushState('stronghold/building/command_center')
            }
        }

        const commandCenter = (
            <CommandCenter
                key={1}
                level={this.props.level}
                handleClick={commandCenterClickHandler}
                handleHover={this.props.isOwn ? this.props.playHoverSound : null}
                isOwn={this.props.isOwn}
            />
        )

        return (
            <div className={styles.base}>
                {this.props.isOwn ?
                    <ClanPanel /> :
                    <ClanInfoPanel
                        stats={this.props.clanStats}
                        clan={this.props.clanInfo}
                        elo_leaderboards={this.props.elo_leaderboards}
                    />
                }
                {this.props.isOwn && (
                    <div className={styles.back}>
                        <Back
                            onClick={this.handleOpenclanlanding}
                            type='back'
                            side='left'
                            caption={BACK_TO_CLAN_INFO}
                        />
                    </div>
                )}
                {this.getRatingLabels()}
                <Gamefield>
                    {this.props.isMigrationScreenVisible && <MigrationScreen />}
                    {this.getDirections()}
                    {this.getTooltips()}
                    <StartingPoint children={[commandCenter]}/>
                </Gamefield>
                {this.props.isOwn && <Toolbar />}
            </div>
        )
    }
}

export const mapStateToProps = (state, ownProps) => {
    let { slots, buildings, stronghold, account, toolbar, userFlags, shop, migrationScreen, clientCounters } = state

    let clanId = ownProps.params && ownProps.params.clanId ? parseInt(ownProps.params.clanId) : state.clan.id

    const isOwn = state.clan.id === clanId
    const hasPermission = isOwn && canManageBuilding(account)

    const { loading, loaded, level, clan: clanInfo, stats: clanStats, elo_leaderboards } = stronghold

    const showLoading = ((loaded !== loading) && loading) || !loaded
    const isShopStateLoaded = shop.productsLoaded || shop.statusLoaded

    slots = Object.keys(slots)
        .reduce((acc, slotId) => ([
            ...acc,
            slots[slotId],
        ]), [])
        .map(slot => ({
            ...slot,
            building: ((slot.building !== null) ? buildings[slot.building] : null),
        }))

    const { popoverOpened } = toolbar

    return {
        slots,
        level,
        showLoading,
        clanId,
        isOwn,
        isShopStateLoaded,
        hasPermission,
        popoverOpened,
        clanInfo,
        clanStats,
        userFlags,
        elo_leaderboards,
        clientCounters,
        isMigrationScreenVisible: migrationScreen.isVisible,
    }
}

export const mapDispatchToProps = (dispatch) => ({
    pushState: (path) => dispatch(push(path)),
    constructionAnimated: (buildingId) => dispatch(constructionAnimated(buildingId)),
    onComponentDidMount: (props) => {
        if (!props.clientCounters.isLoaded) {
            dispatch(requestClientCounters())
        } else if (props.isOwn && props.clientCounters.items.clanNews > 0) {
            dispatch(showMigrationScreen())
        }

        if (props.isOwn) {
            dispatch(requestStronghold())
        } else {
            dispatch(requestStronghold(props.clanId))
        }

        if (!props.isShopStateLoaded) {
            dispatch(requestShopStatus())
        }
    },
    showMigrationScreen: () => dispatch(showMigrationScreen()),
    playHoverSound: () => dispatch(playHoverSound()),
    playBuildingClickSound: () => dispatch(playBuildingClickSound()),
    openClanLanding: () => dispatch(push(CLAN_LANDING)),
    openLeaderboard: (vehicle_level) => dispatch(openLeaderboard(vehicle_level)),
    playExitStrongholdSound: () => dispatch(playExitStrongholdSound()),
})

export default connect(mapStateToProps, mapDispatchToProps)(Stronghold)



// WEBPACK FOOTER //
// ./containers/Stronghold/Stronghold.jsx